import axios from 'axios';
import { axiosInstance } from 'src/axios/axios.singleton';
import { tAxiosCustomConfig } from 'src/axios/axios.types';
import { tFileUploadRequest, tFileUploadResult } from './FileUpload.types.ts';
import { EmptyFileError } from 'src/services/errors/EmptyFileError.ts';

const S3_STORAGE_DOMAIN = 'leadsales-storage.s3.amazonaws.com';
const UPLOAD_DOMAIN = 'uploads.leadsales.services';
const CDN_DOMAIN = import.meta.env.VITE_BASE_FILES_URL as string;
const STORAGE_MANAGEMENT_URL = import.meta.env.VITE_STORAGE_MANAGEMENT_URL as string;

// Canonical media families the storage flow knows. Both direction tables are
// derived from this single source so they cannot drift: MIME → canonical
// (first) extension for the validateMimeType gate, and every extension → MIME
// for files whose browser-reported type is empty (broken OS MIME registries —
// BUG-698).
const MEDIA_TYPE_EXTENSIONS: Record<string, string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/webp': ['webp'],
  'video/mp4': ['mp4'],
  'application/pdf': ['pdf'],
};

const MIME_TO_EXTENSION: Record<string, string> = Object.fromEntries(
  Object.entries(MEDIA_TYPE_EXTENSIONS).map(([mime, extensions]) => [
    mime,
    extensions[0],
  ]),
);

const EXTENSION_TO_MIME: Record<string, string> = Object.fromEntries(
  Object.entries(MEDIA_TYPE_EXTENSIONS).flatMap(([mime, extensions]) =>
    extensions.map((extension) => [extension, mime]),
  ),
);

// Browser/OS MIME type variants that map to a canonical type accepted by the backend.
// Chrome on Windows reports application/x-zip-compressed for .zip files instead of application/zip.
// image/jpg and image/pjpeg are benign JPEG aliases from polluted Windows MIME
// registries; rejecting them blocked valid template header images (BUG-698).
const MIME_NORMALIZATION: Record<string, string> = {
  'application/x-zip-compressed': 'application/zip',
  'application/x-zip': 'application/zip',
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
};

export function normalizeMimeType(rawType: string): string {
  return MIME_NORMALIZATION[rawType] ?? rawType;
}

/**
 * Resolves the MIME type validations and uploads should act on: the normalized
 * browser-reported type, or — when the browser reports none — the type derived
 * from a known file extension. Returns '' when neither source resolves.
 */
export function resolveEffectiveMimeType(file: File): string {
  const normalized = normalizeMimeType(file.type);
  if (normalized) return normalized;
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  return EXTENSION_TO_MIME[extension] ?? '';
}

/**
 * True for axios failures where no HTTP response reached JS at all (status 0:
 * connection blocked or reset — the documented client-firewall class). Timeouts
 * carry ECONNABORTED/ETIMEDOUT, not ERR_NETWORK, so they are excluded. Shared
 * by the S3 PUT retry predicate and the template creator's inline failure
 * classifier so both act on the same error shape.
 */
export function isNetworkUnreachableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, response } = error as { code?: string; response?: unknown };
  return code === 'ERR_NETWORK' && !response;
}

const UPLOAD_TIMEOUT_MS = 30_000;
const PUT_RETRY_WINDOW_MS = 2_000;
const PUT_RETRY_BACKOFF_MS = 500;

function replaceS3DomainWithCDN(url: string): string {
  return url.split(S3_STORAGE_DOMAIN).join(CDN_DOMAIN);
}

// A share flip is one small PATCH; it must not inherit the upload's budget.
const SHARE_TIMEOUT_MS = 10_000;

export const FileUploadService = Object.freeze({
  /**
   * Uploads through a storage ticket and returns only the CDN URL. Kept for
   * the callers that never register a name; the chat uses the variant below.
   */
  uploadFileToS3: async function (
    request: tFileUploadRequest,
  ): Promise<string> {
    const result = await FileUploadService.uploadFileToS3WithRecord(request);
    return result.url;
  },

  /**
   * Uploads through a storage ticket. When `request.name` is set, the ticket
   * carries it and the storage service writes the file's record under that
   * name at ticket time — the moment the name is in hand and there is no second
   * call to lose it in (BUG-862). The result carries the minted file id so a
   * caller can publish the record afterwards with `shareFile`.
   */
  uploadFileToS3WithRecord: async function (
    request: tFileUploadRequest,
  ): Promise<tFileUploadResult> {
    if (!STORAGE_MANAGEMENT_URL) {
      throw new Error('VITE_STORAGE_MANAGEMENT_URL is not configured');
    }
    if (!CDN_DOMAIN) {
      throw new Error('VITE_BASE_FILES_URL is not configured');
    }

    const mimeType = resolveEffectiveMimeType(request.file);

    if (request.validateMimeType) {
      const derivedExtension = MIME_TO_EXTENSION[mimeType];
      if (!derivedExtension) {
        throw new Error(`Unsupported file type: ${mimeType}`);
      }
    }

    const extension = request.file.name.split('.').pop();

    if (!extension || extension === request.file.name) {
      throw new Error(`File has no valid extension: "${request.file.name}"`);
    }

    const fileSize = request.file.size / 1024 / 1024;
    if (fileSize > 100) {
      throw new Error(
        `File exceeds 100 MB limit: ${fileSize.toFixed(2)} MB`,
      );
    }

    // S3 accepts an empty presigned PUT with 200, so a 0-byte file would be
    // stored and sent as a link to nothing (BUG-676).
    if (request.file.size === 0) {
      throw new EmptyFileError(request.file.name);
    }

    // Storage calls go through the authenticated instance: the platform gateway
    // enforces the Firebase token on /storage (BUG-1142). The S3 PUT below stays
    // on bare axios — an extra header would break the presigned signature.
    // A 401 here fails the send; it must not also sign the user out mid-send.
    const ext = extension.startsWith('.') ? extension : `.${extension}`;
    const presignConfig: tAxiosCustomConfig = {
      timeout: UPLOAD_TIMEOUT_MS,
      skipAutoRedirect: true,
    };
    const response = await axiosInstance.post(
      `${STORAGE_MANAGEMENT_URL}/files/${request.workspaceId}/presign`,
      {
        extension: ext,
        contentType: mimeType,
        ...(request.name ? { name: request.name } : {}),
      },
      presignConfig,
    );

    if (!response.data?.data?.uploadUrl) {
      throw new Error('Presigned URL missing from storage service response');
    }

    if (!response.data.data.uploadUrl.startsWith(`https://${S3_STORAGE_DOMAIN}/`)) {
      throw new Error('Unexpected upload destination');
    }

    // Replace only the origin: reserializing the signed path/query can change
    // SigV4 bytes. CloudFront restores this exact S3 Host without signing again.
    const uploadUrl = response.data.data.uploadUrl.replace(
      `https://${S3_STORAGE_DOMAIN}/`,
      `https://${UPLOAD_DOMAIN}/`,
    );

    const uploadFileOptions = {
      headers: {
        'Content-Type': mimeType,
      },
      timeout: UPLOAD_TIMEOUT_MS,
    };

    // One bounded retry for PUTs that die fast with no HTTP response (status 0:
    // client firewall/proxy resets — BUG-698). The elapsed-time window keeps
    // mid-transfer deaths of large uploads from being replayed, and timeouts are
    // excluded by the ERR_NETWORK check. On a failed retry the original axios
    // error is rethrown unwrapped — the template creator's failure classifier
    // depends on the real error shape.
    const putFile = () => axios.put(uploadUrl, request.file, uploadFileOptions);
    const putStartedAt = Date.now();
    try {
      await putFile();
    } catch (error) {
      const failedFast = Date.now() - putStartedAt <= PUT_RETRY_WINDOW_MS;
      if (!isNetworkUnreachableError(error) || !failedFast) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, PUT_RETRY_BACKOFF_MS));
      await putFile();
    }

    const rawUrl = response.data.data.uploadUrl.split('?')[0];

    return {
      url: replaceS3DomainWithCDN(rawUrl),
      // The service returns the id it minted; the key's basename is the same
      // value and covers a response from before the field existed.
      fileId: response.data.data.fileId ?? rawUrl.split('/').pop() ?? '',
      nameRecorded: response.data.data.nameRecorded,
    };
  },

  /**
   * Publishes an uploaded file to the shared folder. Sharing is a second write
   * to the record the ticket already created — never a second record, which is
   * what the cloud callable this replaces used to mint (BUG-862 U4).
   */
  shareFile: async function (
    workspaceId: string,
    fileId: string,
  ): Promise<void> {
    if (!STORAGE_MANAGEMENT_URL) {
      throw new Error('VITE_STORAGE_MANAGEMENT_URL is not configured');
    }

    // The gateway answers 401 to this PATCH without a token (BUG-1142). A 401
    // here must surface as the share warning, not sign the user out mid-send.
    const config: tAxiosCustomConfig = {
      timeout: SHARE_TIMEOUT_MS,
      skipAutoRedirect: true,
    };
    await axiosInstance.patch(
      `${STORAGE_MANAGEMENT_URL}/files/${workspaceId}/${encodeURIComponent(fileId)}`,
      { shared: true },
      config,
    );
  },
});
